import { HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DataSource, ServerDataSource } from "angular2-smart-table";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { Author, AuthorData } from "../interfaces/author";
import { HttpService } from "./common/http.service";
import { User } from "../interfaces/auth/users";
import { unset, omit } from 'lodash';

@Injectable()
export class AuthorService extends AuthorData {

  private readonly url: string;

  get gridDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}`);
  }

  get gridArchivedDataSource(): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?filters[archivedOn][$notNull]=true`);
  }

  constructor(private api: HttpService) {
    super();
    this.url = 'authors';
  }

  getDataSourceOfClient(clientId: number): ServerDataSource {
    return this.api.getServerDataSource(`${environment.API_BASE_URL}/${this.url}?with=deep,2&clients_include_id=${clientId}`);
  }

  listAll(): Observable<Author[]> {
    let params: HttpParams = new HttpParams()
      .append('limit', '-1')
      .append('offset', '0')
      .append('with', 'deep,2');

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  list(pageNumber: number, pageSize: number, query: string = ''): Observable<Author[]> {
    let params: HttpParams = new HttpParams()
      .append('with', 'deep,2');

    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    if (query) {
      params = params.append('where', query);
    }

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  rawQry(pageNumber: number, pageSize: number, params: HttpParams) {
    if (pageSize > 0) {
      params = params.append('limit', `${pageSize}`);
    }

    if (pageNumber > 0) {
      params = params.append('offset', `${(pageNumber - 1) * pageSize}`);
    }

    // 'with' for population, 'where' for filters, 'order' for sorting

    return this.api.get(this.url, { params }).pipe(map(res => res.data || res));
  }

  get(id: any): Observable<Author> {
    return this.api.get(`${this.url}/${id}`, { params: new HttpParams().append('populate', '*') })
      .pipe(map(res => res.data || res));
  }

  create(author: Author): Observable<Author> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    return this.api.post(this.url, { data: author }, params)
      .pipe(map(res => res.data));
  }

  update(author: Author): Observable<Author> {
    let params: HttpParams = new HttpParams()
      .append('populate', '*');
    const changedAuthor = omit(author, ['id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']);
    return this.api.put(`${this.url}/${author.documentId}`, { data: changedAuthor }, params)
      .pipe(map(res => res.data));
  }

  archive(author: Author, date: Date, user: User): Observable<Author> {
    return this.api.put(`${this.url}/${author.documentId}`, {
      data: {
        archivedOn: date,
        archivedBy: user,
      },
    });
  }

  unarchive(author: Author): Observable<Author> {
    return this.api.put(`${this.url}/${author.documentId}`, {
      data: {
        archivedOn: null,
        archivedBy: null,
      },
    });
  }

  delete(id: any): Observable<any> {
    return this.api.delete(`${this.url}/${id}`);
  }
}
